<template>
  <b-container class="product-detail py-4">
    <div v-if="loading" class="text-center py-5">
      <b-spinner large variant="primary"></b-spinner>
    </div>
    
    <div v-else-if="product">
      <b-row>
        <!-- Product Images -->
        <b-col lg="6" class="mb-4">
          <div class="product-images">
            <b-img
              :src="selectedImage"
              :alt="product.name"
              fluid
              class="main-image mb-3"
              @error="onImageError">
            </b-img>
            
            <b-row v-if="product.images && product.images.length > 1">
              <b-col 
                v-for="(image, index) in product.images" 
                :key="index"
                cols="3"
                class="mb-2">
                <b-img 
                  :src="image" 
                  :alt="`${product.name} ${index + 1}`"
                  fluid
                  thumbnail
                  class="thumbnail-image"
                  :class="{ active: selectedImage === image }"
                  @click="selectedImage = image">
                </b-img>
              </b-col>
            </b-row>
          </div>
        </b-col>
        
        <!-- Product Info -->
        <b-col lg="6">
          <h1>{{ product.name }}</h1>
          
          <p class="text-muted" v-if="product.brand">
            Marca: {{ product.brand.name }}
          </p>
          
          <div class="price-section mb-4">
            <h2 class="price" v-if="showPrice">
              ${{ formatPrice(product.price) }}
            </h2>
            <p v-if="product.original_price && product.original_price > product.price">
              <del class="text-muted">${{ formatPrice(product.original_price) }}</del>
              <b-badge variant="success" class="ml-2">
                -{{ calculateDiscount(product.price, product.original_price) }}%
              </b-badge>
            </p>
          </div>
          
          <div class="description mb-4">
            <h5>Descripción</h5>
            <p>{{ product.description || 'Sin descripción disponible' }}</p>
          </div>
          
          <div class="stock-info mb-4">
            <b-badge v-if="hasStock" variant="success" class="p-2">
              <b-icon icon="check-circle"></b-icon>
              Disponible
            </b-badge>
            <b-badge v-else variant="warning" class="p-2">
              <b-icon icon="exclamation-triangle"></b-icon>
              Sin Stock
            </b-badge>
          </div>
          
          <!-- Add to Cart Section -->
          <div v-if="canAddToCart" class="add-to-cart-section">
            <b-row class="align-items-end mb-3">
              <b-col cols="4">
                <b-form-group label="Cantidad">
                  <b-form-spinbutton 
                    v-model="quantity" 
                    min="1" 
                    :max="product.stock || 999"
                    size="lg">
                  </b-form-spinbutton>
                </b-form-group>
              </b-col>
              <b-col cols="8">
                <b-button 
                  variant="success" 
                  size="lg"
                  block
                  @click="addToCart"
                  :disabled="!hasStock">
                  <b-icon icon="cart-plus"></b-icon>
                  Agregar al Carrito
                </b-button>
              </b-col>
            </b-row>
          </div>
          
          <!-- Product Specs -->
          <div v-if="product.specifications" class="specifications mt-4">
            <h5>Especificaciones</h5>
            <b-table-simple small>
              <b-tbody>
                <b-tr v-for="(value, key) in product.specifications" :key="key">
                  <b-td class="font-weight-bold">{{ key }}</b-td>
                  <b-td>{{ value }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </b-col>
      </b-row>
      
      <!-- Related Products -->
      <div v-if="relatedProducts.length > 0" class="related-products mt-5">
        <h3 class="mb-4">Productos Relacionados</h3>
        <b-row>
          <b-col 
            v-for="relatedProduct in relatedProducts" 
            :key="relatedProduct.id"
            cols="12" 
            sm="6" 
            md="3"
            class="mb-4">
            <product-card :product="relatedProduct" />
          </b-col>
        </b-row>
      </div>
    </div>
    
    <b-alert v-else variant="danger" show>
      Producto no encontrado
    </b-alert>
  </b-container>
</template>

<script>
import ProductCard from '@/components/ProductCard.vue'

export default {
  name: 'ProductDetail',
  components: {
    ProductCard
  },
  data() {
    return {
      product: null,
      selectedImage: null,
      quantity: 1,
      loading: true,
      relatedProducts: []
    }
  },
  computed: {
    showPrice() {
      const config = this.$store.getters.shopConfig
      return config && (config.show_public_prices || this.$store.getters.isAuthenticated)
    },
    canAddToCart() {
      const config = this.$store.getters.shopConfig
      return config && (config.allow_sale_unlogged || this.$store.getters.isAuthenticated)
    },
    hasStock() {
      const config = this.$store.getters.shopConfig
      if (config && config.sale_without_stock) {
        return true
      }
      return this.product && this.product.stock && this.product.stock > 0
    }
  },
  created() {
    this.loadProduct()
  },
  watch: {
    '$route.params.id': 'loadProduct'
  },
  methods: {
    onImageError(e) {
      e.target.src = require('@/assets/images/product-placeholder.svg')
    },
    async loadProduct() {
      try {
        this.loading = true
        const response = await this.$axios.get(`/ecommerce/products/getProduct/${this.$route.params.id}`)
        if (response.data.status) {
          this.product = response.data.data
          this.selectedImage = this.product.images && this.product.images[0] || require('@/assets/images/product-placeholder.svg')
          this.loadRelatedProducts()
        }
      } catch (error) {
        console.error('Error loading product:', error)
        this.$bvToast.toast('Error al cargar el producto', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    async loadRelatedProducts() {
      // Cargar productos de la misma categoría
      if (this.product && this.product.category_id) {
        const products = this.$store.state.products.filter(p => 
          p.category_id === this.product.category_id && p.id !== this.product.id
        ).slice(0, 4)
        this.relatedProducts = products
      }
    },
    formatPrice(price) {
      return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price)
    },
    calculateDiscount(price, originalPrice) {
      return Math.round(((originalPrice - price) / originalPrice) * 100)
    },
    addToCart() {
      this.$store.dispatch('addToCart', {
        id: this.product.id,
        name: this.product.name,
        price: this.product.price,
        image: this.selectedImage,
        quantity: this.quantity
      })
      
      this.$bvToast.toast(`${this.quantity} ${this.product.name} agregado(s) al carrito`, {
        title: 'Éxito',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000
      })
    }
  }
}
</script>

<style scoped>
.main-image {
  width: 100%;
  max-height: 500px;
  object-fit: contain;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.thumbnail-image {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.thumbnail-image:hover,
.thumbnail-image.active {
  opacity: 1;
  border-color: #007bff;
}

.price {
  color: #28a745;
  font-weight: bold;
}

.specifications td {
  padding: 0.5rem;
}
</style>